import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    api.getCmsPlans()
      .then(res => setPlans(res.plans || []))
      .catch(err => console.error('Error loading plans:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleEdit = (plan) => {
    setEditingPlan({ ...plan });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.updateCmsPlan(editingPlan.id, {
        name: editingPlan.name,
        price: editingPlan.price,
        description: editingPlan.description,
        popular: editingPlan.popular
      });
      setPlans(prev => prev.map(p => p.id === editingPlan.id ? editingPlan : p));
      setEditingPlan(null);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save plan.');
    }
  };

  if (loading) {
    return <p className="text-zinc-500 text-sm">Loading plans...</p>;
  }

  return (
    <div className="space-y-8 font-sans">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-white">Subscription Plans & Pricing</h2>
          <p className="text-xs text-zinc-500 mt-1 font-light">Manage client intelligence subscription tiers and pricing copy.</p>
        </div>
      </div>

      {savedSuccess && (
        <div className="p-4 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 text-xs rounded-xl flex items-center gap-2">
          <SafeIcon name="CheckCircle" className="w-4 h-4" />
          Plan settings updated successfully.
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans.map(plan => (
          <div key={plan.id} className="bg-zinc-950 border border-zinc-900 rounded-3xl p-6 flex flex-col justify-between shadow-xl relative overflow-hidden">
            {plan.popular && (
              <span className="absolute top-4 right-4 bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                Most Popular
              </span>
            )}

            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{plan.id}</span>
                <h3 className="text-xl font-bold text-white mt-1">{plan.name}</h3>
                <p className="text-2xl font-bold text-blue-400 mt-2">{plan.price}</p>
                <p className="text-xs text-zinc-400 font-light mt-2 leading-relaxed">{plan.description}</p>
              </div>

              <div className="pt-4 border-t border-zinc-900 space-y-2">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Features Included:</p>
                <ul className="space-y-1.5">
                  {(plan.features || []).map((feat, idx) => (
                    <li key={idx} className="text-xs text-zinc-300 flex items-center gap-2">
                      <SafeIcon name="Check" className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-zinc-900">
              <button
                onClick={() => handleEdit(plan)}
                className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon name="Edit" className="w-3.5 h-3.5" />
                Edit Plan
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingPlan && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-lg w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">Edit {editingPlan.name}</h3>
              <button onClick={() => setEditingPlan(null)} className="text-zinc-500 hover:text-white">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Plan Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingPlan.name}
                  onChange={e => setEditingPlan({ ...editingPlan, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Pricing Display</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingPlan.price}
                  onChange={e => setEditingPlan({ ...editingPlan, price: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Description</label>
                <textarea
                  rows={3}
                  className="input-field resize-none"
                  value={editingPlan.description}
                  onChange={e => setEditingPlan({ ...editingPlan, description: e.target.value })}
                  required
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="popular-check"
                  checked={editingPlan.popular || false}
                  onChange={e => setEditingPlan({ ...editingPlan, popular: e.target.checked })}
                  className="rounded bg-zinc-900 border-zinc-800 text-blue-600"
                />
                <label htmlFor="popular-check" className="text-xs text-zinc-300">Set as "Most Popular" Plan</label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button
                  type="button"
                  onClick={() => setEditingPlan(null)}
                  className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
